import { useState } from "react";

const PAGE_DEFAULT = 1;

const useFetchingImport = <
  TRequest,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TResponse extends RestAPIResponse<{ items: any[]; total?: number }>
>({
  serviceFn,
  totalEachRequest,
  filters,
  configs,
}: {
  serviceFn: (
    params: TRequest & {
      page: number;
      limit: number;
      total?: number;
      isExport?: boolean;
    }
  ) => Promise<TResponse>;
  totalEachRequest: number;
  filters: TRequest;
  configs?: { isExport?: boolean };
}) => {
  const [loading, setLoading] = useState<boolean>(false);

  const fetchAllData = async (extendFilter?: TRequest) => {
    try {
      setLoading(true);
      const currentFilters = { ...filters, ...extendFilter };
      let hasMore = true;
      let page = PAGE_DEFAULT;
      let results: TResponse["data"]["items"] =
        [] as TResponse["data"]["items"];

      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const res = await serviceFn({
          ...currentFilters,
          page,
          limit: totalEachRequest,
          ...(configs?.isExport && page === 1 && { isExport: true }),
        });
        if (res?.data?.items?.length < totalEachRequest) {
          hasMore = false;
        }

        if (res?.data?.items?.length) {
          results = [...results, ...res.data.items];
        }

        page += 1;
      }
      setLoading(false);
      return results;
    } catch {
      setLoading(false);
      return [];
    }
  };

  const fetchAllDataWithHeaders = async (extendFilter?: Partial<TRequest>) => {
    try {
      setLoading(true);
      const currentFilters = { ...filters, ...extendFilter };
      let hasMore = true;
      let page = PAGE_DEFAULT;
      let results: TResponse["data"]["items"] =
        [] as TResponse["data"]["items"];
      let headers = {} as Record<string, string>;

      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const res = await serviceFn({
          ...currentFilters,
          page,
          limit: totalEachRequest,
          ...(configs?.isExport && page === 1 && { isExport: true }),
        });
        if (res?.data?.items?.length < totalEachRequest) {
          hasMore = false;
        }

        if (res?.data?.items?.length) {
          results = [...results, ...res.data.items];
        }

        headers = {
          ...headers,
          ...(res?.data as unknown as { headers: Record<string, string> })
            ?.headers,
        };

        page += 1;
      }
      setLoading(false);
      return {
        items: results,
        headers,
      };
    } catch {
      setLoading(false);
      return {
        items: [],
        headers: {},
      };
    }
  };

  return {
    loading,
    fetchAllData,
    fetchAllDataWithHeaders,
  };
};

export default useFetchingImport;
